/**
 * Twilio Call Analytics Client
 * Purpose: Marketing analytics for the existing Twilio phone system
 * 
 * This client:
 * - Queries the existing 'calls' table (read-only)
 * - Provides marketing-specific views and analytics
 * - Calculates speed-to-lead and response metrics
 * - Does NOT create or manage Twilio resources (admin system owns that)
 */

import { supabase } from './supabase';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface Call {
  call_sid: string;
  from_number: string;
  to_number: string;
  status: string;
  direction: 'inbound' | 'outbound';
  started_at: string;
  ended_at: string | null;
  answered: boolean;
  disposition: string | null;
  recording_url: string | null;
  transcription_url: string | null;
  queue_name: string | null;
  company_name: string | null;
}

export interface MarketingCall extends Call {
  // Marketing enrichment
  marketing_source?: string;
  campaign_id?: string;
  keyword?: string;
  gclid?: string;
  
  // Speed-to-lead
  first_response_at?: string;
  first_response_method?: string;
  response_time_seconds?: number;
  speed_to_lead_grade?: 'excellent' | 'good' | 'poor' | 'failed';
  
  // Outcome
  lead_status?: string;
  booked_job_id?: string;
  booking_value?: number;
  estimated_booking_value?: number;
  cost_per_call?: number;
}

export interface CallMetrics {
  total_calls: number;
  answered_calls: number;
  missed_calls: number;
  answer_rate: number;
  avg_response_time_seconds: number;
  
  // Speed-to-lead breakdown
  excellent_responses: number; // <60s
  good_responses: number; // 1-5min
  poor_responses: number; // >5min
  failed_responses: number; // Never responded
  
  // By source
  calls_by_source: Record<string, number>;
  bookings_by_source: Record<string, number>;
  
  // Revenue
  total_booking_value: number;
  avg_booking_value: number;
}

export interface SpeedToLeadViolation {
  id: string;
  call_sid: string;
  company_name: string;
  violation_type: 'missed_call' | 'slow_response' | 'no_followup';
  threshold_seconds: number;
  actual_seconds: number | null;
  severity: 'warning' | 'critical' | 'urgent';
  alerted: boolean;
  resolved: boolean;
  created_at: string;
}

// ============================================================================
// Twilio Call Analytics Client Class
// ============================================================================

export class TwilioCallAnalytics {
  
  /**
   * Get calls for a date range
   */
  async getCalls(params: {
    companyName?: string;
    startDate: string;
    endDate: string;
    direction?: 'inbound' | 'outbound';
    answered?: boolean;
  }): Promise<Call[]> {
    let query = supabase
      .from('calls')
      .select('*')
      .gte('started_at', params.startDate)
      .lte('started_at', params.endDate)
      .order('started_at', { ascending: false });
    
    if (params.companyName) {
      query = query.eq('company_name', params.companyName);
    }
    
    if (params.direction) {
      query = query.eq('direction', params.direction);
    }
    
    if (params.answered !== undefined) {
      query = query.eq('answered', params.answered);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('[TwilioCallAnalytics] Error fetching calls:', error);
      throw error;
    }
    
    return data || [];
  }

  /**
   * Get today's calls for real-time monitoring
   */
  async getTodaysCalls(companyName?: string): Promise<Call[]> {
    const today = new Date().toISOString().split('T')[0];
    return this.getCalls({
      companyName,
      startDate: `${today}T00:00:00Z`,
      endDate: `${today}T23:59:59Z`,
    });
  }

  /**
   * Get enriched marketing calls (with analytics data)
   */
  async getMarketingCalls(params: {
    companyName?: string;
    startDate: string;
    endDate: string;
  }): Promise<MarketingCall[]> {
    let query = supabase
      .from('marketing_call_analytics')
      .select(`
        *,
        calls!inner (
          call_sid,
          from_number,
          to_number,
          status,
          direction,
          started_at,
          ended_at,
          answered,
          disposition,
          recording_url,
          transcription_url,
          queue_name,
          company_name
        )
      `)
      .gte('calls.started_at', params.startDate)
      .lte('calls.started_at', params.endDate)
      .order('created_at', { ascending: false });
    
    if (params.companyName) {
      query = query.eq('company_name', params.companyName);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('[TwilioCallAnalytics] Error fetching marketing calls:', error);
      throw error;
    }
    
    // Flatten the nested structure
    return (data || []).map((row: any) => ({
      ...row.calls,
      ...row,
    }));
  }

  /**
   * Get call metrics for a date range
   */
  async getCallMetrics(params: {
    companyName?: string;
    startDate: string;
    endDate: string;
  }): Promise<CallMetrics> {
    const calls = await this.getCalls(params);
    const marketingCalls = await this.getMarketingCalls(params);
    
    const total = calls.length;
    const answered = calls.filter(c => c.answered).length;
    const missed = total - answered;
    
    // Calculate response metrics
    const withResponseTime = marketingCalls.filter(c => c.response_time_seconds !== null);
    const avgResponseTime = withResponseTime.length > 0
      ? withResponseTime.reduce((sum, c) => sum + (c.response_time_seconds || 0), 0) / withResponseTime.length
      : 0;
    
    // Speed-to-lead breakdown
    const excellent = marketingCalls.filter(c => c.speed_to_lead_grade === 'excellent').length;
    const good = marketingCalls.filter(c => c.speed_to_lead_grade === 'good').length;
    const poor = marketingCalls.filter(c => c.speed_to_lead_grade === 'poor').length;
    const failed = marketingCalls.filter(c => c.speed_to_lead_grade === 'failed').length;
    
    // Group by source
    const callsBySource: Record<string, number> = {};
    const bookingsBySource: Record<string, number> = {};
    
    marketingCalls.forEach(call => {
      const source = call.marketing_source || 'direct';
      callsBySource[source] = (callsBySource[source] || 0) + 1;
      
      if (call.booked_job_id) {
        bookingsBySource[source] = (bookingsBySource[source] || 0) + 1;
      }
    });
    
    // Revenue metrics
    const bookedCalls = marketingCalls.filter(c => c.booking_value);
    const totalRevenue = bookedCalls.reduce((sum, c) => sum + (c.booking_value || 0), 0);
    
    return {
      total_calls: total,
      answered_calls: answered,
      missed_calls: missed,
      answer_rate: total > 0 ? (answered / total) * 100 : 0,
      avg_response_time_seconds: Math.round(avgResponseTime),
      excellent_responses: excellent,
      good_responses: good,
      poor_responses: poor,
      failed_responses: failed,
      calls_by_source: callsBySource,
      bookings_by_source: bookingsBySource,
      total_booking_value: totalRevenue,
      avg_booking_value: bookedCalls.length > 0 ? totalRevenue / bookedCalls.length : 0,
    };
  }

  /**
   * Get speed-to-lead violations
   */
  async getViolations(params: {
    companyName?: string;
    resolved?: boolean;
    severity?: 'warning' | 'critical' | 'urgent';
  }): Promise<SpeedToLeadViolation[]> {
    let query = supabase
      .from('speed_to_lead_violations')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (params.companyName) {
      query = query.eq('company_name', params.companyName);
    }
    
    if (params.resolved !== undefined) {
      query = query.eq('resolved', params.resolved);
    }
    
    if (params.severity) {
      query = query.eq('severity', params.severity);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('[TwilioCallAnalytics] Error fetching violations:', error);
      throw error;
    }
    
    return data || [];
  }

  /**
   * Create marketing call analytics record
   */
  async createMarketingCallAnalytics(data: {
    call_sid: string;
    company_name: string;
    marketing_source?: string;
    campaign_id?: string;
    keyword?: string;
    gclid?: string;
    fbclid?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    landing_page_url?: string;
    referrer_url?: string;
  }) {
    const { data: result, error } = await supabase
      .from('marketing_call_analytics')
      .insert({
        ...data,
        call_received_at: new Date().toISOString(),
      })
      .select()
      .single();
    
    if (error) {
      console.error('[TwilioCallAnalytics] Error creating marketing call analytics:', error);
      throw error;
    }
    
    return result;
  }

  /**
   * Update marketing call analytics
   */
  async updateMarketingCallAnalytics(callSid: string, updates: {
    first_response_at?: string;
    first_response_method?: 'sms' | 'callback' | 'email';
    sms_sent?: boolean;
    callback_scheduled?: boolean;
    lead_status?: string;
    booked_job_id?: string;
    booking_value?: number;
  }) {
    // Calculate response time if first_response_at is provided
    let additionalUpdates = {};
    if (updates.first_response_at) {
      const { data: analytics } = await supabase
        .from('marketing_call_analytics')
        .select('call_received_at')
        .eq('call_sid', callSid)
        .single();
      
      if (analytics) {
        const receivedAt = new Date(analytics.call_received_at);
        const respondedAt = new Date(updates.first_response_at);
        const responseTime = Math.floor((respondedAt.getTime() - receivedAt.getTime()) / 1000);
        additionalUpdates = { response_time_seconds: responseTime };
      }
    }
    
    const { data, error } = await supabase
      .from('marketing_call_analytics')
      .update({
        ...updates,
        ...additionalUpdates,
        updated_at: new Date().toISOString(),
      })
      .eq('call_sid', callSid)
      .select()
      .single();
    
    if (error) {
      console.error('[TwilioCallAnalytics] Error updating marketing call analytics:', error);
      throw error;
    }
    
    return data;
  }

  /**
   * Create speed-to-lead violation
   */
  async createViolation(data: {
    call_sid: string;
    company_name: string;
    violation_type: 'missed_call' | 'slow_response' | 'no_followup';
    threshold_seconds: number;
    actual_seconds?: number;
    severity: 'warning' | 'critical' | 'urgent';
    description?: string;
  }) {
    const { data: result, error } = await supabase
      .from('speed_to_lead_violations')
      .insert(data)
      .select()
      .single();
    
    if (error) {
      console.error('[TwilioCallAnalytics] Error creating violation:', error);
      throw error;
    }
    
    return result;
  }

  /**
   * Resolve a violation
   */
  async resolveViolation(id: string, resolvedBy: string, notes?: string) {
    const { data, error } = await supabase
      .from('speed_to_lead_violations')
      .update({
        resolved: true,
        resolved_at: new Date().toISOString(),
        resolved_by: resolvedBy,
        resolution_notes: notes,
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) {
      console.error('[TwilioCallAnalytics] Error resolving violation:', error);
      throw error;
    }
    
    return data;
  }
}

// ============================================================================
// Singleton Instance & Helper Functions
// ============================================================================

let analyticsInstance: TwilioCallAnalytics | null = null;

/**
 * Get or create TwilioCallAnalytics instance
 */
export function getTwilioCallAnalytics(): TwilioCallAnalytics {
  if (!analyticsInstance) {
    analyticsInstance = new TwilioCallAnalytics();
  }
  return analyticsInstance;
}

/**
 * Helper: Format phone number for display
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

/**
 * Helper: Calculate minutes since a timestamp
 */
export function getMinutesSince(timestamp: string): number {
  const then = new Date(timestamp);
  const now = new Date();
  return Math.floor((now.getTime() - then.getTime()) / 1000 / 60);
}

/**
 * Helper: Get today's date range
 */
export function getTodayDateRange(): { startDate: string; endDate: string } {
  const today = new Date().toISOString().split('T')[0];
  return {
    startDate: `${today}T00:00:00Z`,
    endDate: `${today}T23:59:59Z`,
  };
}

/**
 * Helper: Get date range for last N days
 */
export function getDateRange(days: number): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}
